import React from "react"

import {
  useLenderInvitationCreate,
  useLenderUsersListSuspense,
  useLenderUsersPartialUpdate,
} from "@boundless/bl-api-client/lender/services"
import {
  usePartnersInvitationCreate,
  usePartnersUsersListSuspense,
  usePartnersUsersPartialUpdate,
} from "@boundless/bl-api-client/partners/services"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import type { ErrorType } from "@/lib/clients"

import type { User } from "@boundless/bl-api-client/common/models"
import type { LenderInvitationCreateBody } from "@boundless/bl-api-client/lender/services.zod"
import type { PartnersInvitationCreateBody } from "@boundless/bl-api-client/partners/services.zod"
import type { z } from "zod"

export type InviteSchema = z.infer<
  typeof PartnersInvitationCreateBody | typeof LenderInvitationCreateBody
>

interface InviteCallback {
  data: InviteSchema
  onSuccess: () => void
  onError: (error: ErrorType<unknown>) => void
}

interface UserContextType {
  users: User[]
  setActive: (uuid: string, isActive: boolean) => void
  isUpdatePending: boolean
  invite: ({ data, onSuccess, onError }: InviteCallback) => void
  isInvitePending: boolean
}

const UserContext = React.createContext<UserContextType | undefined>(undefined)

type UserListQuery =
  | typeof usePartnersUsersListSuspense
  | typeof useLenderUsersListSuspense
type InviteMutation =
  | typeof usePartnersInvitationCreate
  | typeof useLenderInvitationCreate
type UpdateMutation =
  | typeof usePartnersUsersPartialUpdate
  | typeof useLenderUsersPartialUpdate

function createUserProvider(
  userListQuery: UserListQuery,
  updateMutation: UpdateMutation,
  inviteMutation: InviteMutation,
) {
  return function UserProvider({ children }: React.PropsWithChildren) {
    const queryClient = useQueryClient()

    const { data: users, queryKey } = userListQuery()

    const { mutate: updateUser, isPending: isUpdatePending } = updateMutation({
      mutation: {
        onSuccess: (data: User) => {
          const updatedList = users.map((user) =>
            user.uuid === data.uuid ? { ...user, ...data } : user,
          )
          queryClient.setQueryData(queryKey, updatedList)
        },
      },
    })

    const { mutate: sendInvite, isPending: isInvitePending } = inviteMutation({
      mutation: {
        onSuccess: (data) => {
          queryClient.invalidateQueries({ queryKey: queryKey })
          toast.success("Invitation sent successfully!", {
            description: `${data.firstName} will receive an email shortly.`,
          })
        },
      },
    })

    const setActive = React.useCallback((uuid: string, isActive: boolean) => {
      updateUser({ uuid, data: { isActive: isActive } })
    }, [])

    const invite = React.useCallback(
      ({ data, onSuccess, onError }: InviteCallback) => {
        sendInvite({ data }, { onSuccess, onError })
      },
      [],
    )

    return (
      <UserContext.Provider
        value={{ users, invite, isInvitePending, setActive, isUpdatePending }}
      >
        {children}
      </UserContext.Provider>
    )
  }
}

export const PartnerUserProvider = createUserProvider(
  usePartnersUsersListSuspense,
  usePartnersUsersPartialUpdate,
  usePartnersInvitationCreate,
)

export const LenderUserProvider = createUserProvider(
  useLenderUsersListSuspense,
  useLenderUsersPartialUpdate,
  useLenderInvitationCreate,
)

export const useUsers = () => {
  const context = React.useContext(UserContext)
  if (context === undefined) {
    throw new Error("useUsers must be used within a UserProvider")
  }
  return context
}
